import { FC, useCallback, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { InvalidValue } from "../InvalidValue";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { Input } from "./Input";
import { SsnTextboxProps } from "./SsnTextbox";
import { errorMessages } from "./errorMessages";
import { VALIDATION } from "capitalrx-components-2.0";

/**
 * The `Field` component properties.
 */
export interface FieldProps
  extends FieldBaseProps<string | InvalidValue | null>,
    Pick<SsnTextboxProps, "showToggle" | "masked" | "placeholderChar"> {}

/**
 * A React Hook Form compatible SsnTextbox Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, required = false, defaultValue = null, validator, ...restProps } = props;
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const sanitizeSSN = (value: string): string => {
    // Remove any non-digit characters
    return value.replace(/\D/g, "").slice(0, 9);
  };

  const validateHandler = useCallback(
    (value: string | InvalidValue | null, formValues: FieldValues) => {
      if (required && (!value || value === "")) return VALIDATION.REQUIRED;

      if (typeof value === "string" ) {
        const sanitizedValue = sanitizeSSN(value);
        if (sanitizedValue.length !== 9) return errorMessages["invalid length"];
        return validator ? validator(typeof value === "string" ? sanitizeSSN(value) : value, formValues) : true;
      }

      if (value !== null) return errorMessages["invalid format"];

      return validator ? validator(typeof value === "string" ? sanitizeSSN(value) : value, formValues) : true;
    },
    [required, validator],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [disabled, readOnly, validateHandler],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled, readOnly, resetField, name]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  const changeHandler = useCallback(
    (newValue: string | InvalidValue | null) => {
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          value={value}
          required={required}
          defaultValue={defaultValue}
          onChange={changeHandler}
          onFocusExit={onBlur}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
